import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  EventNote as LeaveIcon,
  Inventory as AssetIcon,
  PeopleAlt as HRIcon,
  Receipt as ExpenseIcon,
  Description as FormIcon,
  DateRange as CalendarIcon,
  Inbox as InboxIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useInbox } from '../context/InboxContext';

interface NavMenuItem {
  label: string;
  icon?: React.ReactNode;
  path?: string;
  submenu?: NavMenuItem[];
  roles?: string[];
}

const MobileBottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { unreadCount } = useInbox();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const themeColors = {
    surface: isDarkMode ? '#1e293b' : '#f8fafc',
    text: isDarkMode ? '#f1f5f9' : '#1e293b',
    textSecondary: isDarkMode ? '#cbd5e1' : '#64748b',
    textTertiary: isDarkMode ? '#94a3b8' : '#94a3b8',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    accent: '#10d7e2',
    accentLight: '#2fdfeb',
    accentHover: isDarkMode ? 'rgba(16, 215, 226, 0.15)' : 'rgba(16, 215, 226, 0.1)',
    accentHoverDark: isDarkMode ? 'rgba(16, 215, 226, 0.2)' : 'rgba(16, 215, 226, 0.12)'
  };

  const allMenuItems: NavMenuItem[] = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon sx={{ fontSize: '20px' }} />,
      path: '/dashboard',
      roles: ['admin', 'user', 'manager']
    },
    {
      label: 'My Inbox',
      icon: (
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : undefined}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#f44336',
              color: '#ffffff',
              fontSize: '9px',
              height: '14px',
              minWidth: '14px',
              borderRadius: '7px'
            }
          }}
        >
          <InboxIcon sx={{ fontSize: '20px' }} />
        </Badge>
      ),
      path: '/inbox',
      roles: ['admin', 'user', 'manager']
    },
    {
      label: 'Leave Management',
      icon: <LeaveIcon sx={{ fontSize: '20px' }} />,
      roles: ['admin', 'user', 'manager'],
      submenu: [
        { label: 'Dashboard', path: '/leaves/dashboard', roles: ['admin', 'manager'] },
        { label: 'Apply Leave', path: '/leaves/apply', roles: ['user', 'manager', 'admin'] },
        { label: 'My Leaves', path: '/leaves/my-leaves', roles: ['user', 'manager', 'admin'] }
      ]
    },
    {
      label: 'Asset Management',
      icon: <AssetIcon sx={{ fontSize: '20px' }} />,
      roles: ['admin', 'user'],
      submenu: [
        { label: 'View Assets', path: '/assets/view', roles: ['admin', 'manager'] },
        { label: 'Manage Asset', path: '/assets/assign', roles: ['admin'] },
        { label: 'My Assets', path: '/assets/my-assets', roles: ['user', 'admin', 'manager'] }
      ]
    },
    {
      label: 'HR Management',
      icon: <HRIcon sx={{ fontSize: '20px' }} />,
      path: '/hr/employees',
      roles: ['admin']
    },
    {
      label: 'Resource Directory',
      icon: <HRIcon sx={{ fontSize: '20px' }} />,
      path: '/resources/directory',
      roles: ['admin', 'manager']
    },
    {
      label: 'Expense Claims',
      icon: <ExpenseIcon sx={{ fontSize: '20px' }} />,
      roles: ['admin', 'user', 'manager'],
      submenu: [
        { label: 'Dashboard', path: '/expenses/dashboard', roles: ['admin', 'manager'] },
        { label: 'My Claims', path: '/expenses/my-claims', roles: ['user', 'manager', 'admin'] }
      ]
    },
    {
      label: 'Form Management',
      icon: <FormIcon sx={{ fontSize: '20px' }} />,
      roles: ['admin', 'user'],
      submenu: [
        { label: 'Manage Forms', path: '/forms/manage', roles: ['admin'] },
        { label: 'My Forms', path: '/forms/my-forms', roles: ['user', 'admin'] }
      ]
    },
    {
      label: 'Holiday Management',
      icon: <CalendarIcon sx={{ fontSize: '20px' }} />,
      roles: ['admin', 'user'],
      submenu: [
        { label: 'Holiday Calendar', path: '/holidays/calendar', roles: ['user', 'admin'] },
        { label: 'Manage Holidays', path: '/holidays/manage', roles: ['admin'] }
      ]
    },
    {
      label: 'Ticket Management',
      icon: <CalendarIcon sx={{ fontSize: '20px' }} />,
      roles: ['admin', 'user', 'manager'],
      submenu: [
        { label: 'Raise Ticket', path: '/tickets/raise', roles: ['admin', 'user', 'manager'] },
        { label: 'My Tickets', path: '/tickets/my-tickets', roles: ['admin', 'user', 'manager'] }
      ]
    }
  ];

  const isMenuVisible = (item: NavMenuItem): boolean => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role || '');
  };

  const filterMenuItems = (items: NavMenuItem[]): NavMenuItem[] => {
    return items.filter(isMenuVisible).map((item) => ({
      ...item,
      submenu: item.submenu ? filterMenuItems(item.submenu) : undefined
    }));
  };

  const visibleMenuItems = filterMenuItems(allMenuItems);

  const toggleMenuExpansion = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((m) => m !== label) : [...prev, label]
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <Box
        sx={{
          display: 'none',
          '@media (max-width: 640px)': {
            display: 'flex',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60px',
            backgroundColor: themeColors.surface,
            borderTop: `1px solid ${themeColors.border}`,
            zIndex: 1000,
            justifyContent: 'space-around',
            alignItems: 'center',
            boxShadow: `0 -1px 3px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'}`
          }
        }}
      >
        {/* Dashboard */}
        <IconButton
          onClick={() => handleNavigate('/dashboard')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            color: isActive('/dashboard') ? themeColors.accent : themeColors.textSecondary,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <DashboardIcon sx={{ fontSize: '24px' }} />
          <Typography sx={{ fontSize: '10px', fontWeight: isActive('/dashboard') ? 600 : 400 }}>
            Home
          </Typography>
        </IconButton>

        {/* Inbox */}
        <IconButton
          onClick={() => handleNavigate('/inbox')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            color: isActive('/inbox') ? themeColors.accent : themeColors.textSecondary,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <InboxIcon sx={{ fontSize: '24px' }} />
          <Typography sx={{ fontSize: '10px', fontWeight: isActive('/inbox') ? 600 : 400 }}>
            Inbox
          </Typography>
        </IconButton>

        {/* More Menu */}
        <IconButton
          onClick={() => setMobileMenuOpen(true)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            color: themeColors.textSecondary,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <MenuIcon sx={{ fontSize: '24px' }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 400 }}>
            Menu
          </Typography>
        </IconButton>
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="bottom"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: 'none',
          '@media (max-width: 640px)': {
            display: 'block'
          },
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxHeight: '90vh',
            backgroundColor: themeColors.surface
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${themeColors.border}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ width: '100%', maxWidth: '100%', overflow: 'auto', mb: 8 }}>
          {visibleMenuItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.submenu ? (
                <>
                  <ListItemButton
                    onClick={() => toggleMenuExpansion(item.label)}
                    sx={{
                      py: 1.5,
                      color: themeColors.text,
                      '&:hover': {
                        backgroundColor: themeColors.accentHover
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: themeColors.accent, minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 500 } }}
                    />
                    {expandedMenus.includes(item.label) ? <ExpandLess sx={{ color: themeColors.accent }} /> : <ExpandMore sx={{ color: themeColors.accent }} />}
                  </ListItemButton>

                  <Collapse in={expandedMenus.includes(item.label)} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.submenu?.map((subitem, subindex) => (
                        <ListItemButton
                          key={subindex}
                          onClick={() => handleNavigate(subitem.path || '/')}
                          selected={isActive(subitem.path)}
                          sx={{
                            pl: 4,
                            py: 1,
                            color: isActive(subitem.path) ? themeColors.accent : themeColors.text,
                            backgroundColor: isActive(subitem.path) ? themeColors.accentHoverDark : 'transparent',
                            fontSize: '13px',
                            '&:hover': {
                              backgroundColor: themeColors.accentHover
                            }
                          }}
                        >
                          <ListItemText
                            primary={subitem.label}
                            primaryTypographyProps={{ sx: { fontSize: '13px', fontWeight: 400 } }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItemButton
                  onClick={() => handleNavigate(item.path || '/')}
                  selected={isActive(item.path)}
                  sx={{
                    py: 1.5,
                    color: isActive(item.path) ? themeColors.accent : themeColors.text,
                    backgroundColor: isActive(item.path) ? themeColors.accentHoverDark : 'transparent',
                    '&:hover': {
                      backgroundColor: themeColors.accentHover
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: isActive(item.path) ? themeColors.accent : themeColors.textSecondary, minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 500 } }}
                  />
                </ListItemButton>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default MobileBottomNav;
